import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { firstValueFrom } from 'rxjs';

import { ProcessingComponent } from '../';

import { getScientificName, isEmpty } from '../../common/functions';
import { notifyAndTrackError } from '../../common/error';

import { AppInsightsService } from '../../services/app-insights.service';
import { ITaxon, ITaxonResponse, TaxonService } from '../../services/insecta';

@Component({
  selector: 'app-taxon-search',
  standalone: true,
  templateUrl: `taxon-search.component.html`,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    FloatLabelModule,
    FormsModule,
    IftaLabelModule,
    IconFieldModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputIconModule,
    InputTextModule,
    TableModule,
    SelectModule,
    ReactiveFormsModule,
    ProcessingComponent
  ],
  providers: [MessageService]
})
export class TaxonSearchComponent implements OnInit, OnChanges {
  @Input() disabled: boolean = false;
  @Input('taxon') taxon: ITaxon | null = null;
  @Output('taxonChange') taxonChange = new EventEmitter<any>();

  scientificName: string | null = null;
  selectedTaxon: ITaxon | null = null;

  dialogVars: any = {
    searchButtonClick: false,
    displayData: null,
    response: null,
    visible: false
  };

  pageVars: any = {
    isReady: false
  };

  searchArgs: any = {
    name: null,
    matchType: { name: 'Starts With', value: 'startsWith' },
    pageNo: 1,
    pageSz: 10
  };

  matchTypes: any[] = [
    { name: 'Exact', value: 'exact' },
    { name: 'Starts With', value: 'startsWith' }
  ];

  constructor(
    private service: TaxonService,
    private appInsights: AppInsightsService,
    private msgService: MessageService
  ) {}

  ngOnInit() {
    this.scientificName = getScientificName(<ITaxon>this.taxon);
    this.pageVars.isReady = true;
  }

  ngOnChanges() {
    this.scientificName = getScientificName(<ITaxon>this.taxon);
  }

  modalShow() {
    this.searchArgs = {
      name: null,
      matchType: { name: 'Starts With', value: 'startsWith' },
      pageNo: 1,
      pageSz: 50
    };
    this.dialogVars.visible = true;
  }

  modalExit() {
    this.dialogVars.displayData = null;
    this.dialogVars.response = null;
    this.dialogVars.visible = false;
  }

  clearTaxon() {
    this.taxon = null;
    this.scientificName = null;
    this.taxonChange.emit(null);
  }

  searchTaxonClick() {
    if (this.isSearchValid() === false) return;
    this.searchArgs.pageNo = 0;
    this.searchArgs.pageSz = 10;
    this.searchTaxon();
  }

  searchTaxon() {
    if (isEmpty(this.searchArgs.name)) {
      return;
    }

    this.pageVars.isReady = false;
    firstValueFrom(this.service.getTaxa(this.searchArgs.name, this.searchArgs.matchType.value, this.searchArgs.pageNo, this.searchArgs.pageSz))
      .then((response: ITaxonResponse) => {
        this.dialogVars.response = response;
        this.dialogVars.displayData = this.dialogVars.response.data.map(
          (t: ITaxon) =>
            <ITaxonDisplay>{
              id: t.id,
              scientificName: getScientificName(t),
              division: t.division,
              rank: t.rank
            }
        );
      })
      .catch((err) => {
        this.pageVars.displayData = null;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TaxonSearchComponent', action: 'SearchTaxa:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  selectTaxon(t: ITaxonDisplay) {
    const taxon = this.dialogVars.response.data.find((x: ITaxon) => x.id === t.id);
    this.selectedTaxon = taxon;
    this.scientificName = getScientificName(taxon);

    this.taxonChange.emit(taxon);
    this.modalExit();
  }

  isSearchValid() {
    if (isEmpty(this.searchArgs.name)) return false;
    if (isEmpty(this.searchArgs.matchType)) return false;
    return true;
  }

  handleGridEvent(event: any) {
    this.searchArgs.pageNo = Math.trunc((event.first ?? 0) / 10) + 1;
    this.searchTaxon();
  }
}

interface ITaxonDisplay {
  id?: number;
  scientificName: string;
  division: string;
  rank: string;
}
